import React, { useState, useRef, useEffect, useMemo } from 'react';
import { IconButton, TextField, Typography, CircularProgress, Tooltip, Box } from '@mui/material';
import { Send, Assistant } from '@mui/icons-material';
import { ChatProps } from '../../types/chat';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { DEFAULT_SUGGESTED_QUESTIONS } from '../../constants/suggestedQuestions';
import { Suggestions } from './Suggestions';

import {
  ChatContainer,
  MessageList,
  InputContainer,
  TypingIndicator,
  FloatingButton,
} from './Chat.styles';
import { ChatHeader } from './ChatHeader';
import { ChatMessage } from './ChatMessage';
import { TrialOverlay } from './TrialOverlay';
import { TrialStatusBanner } from './TrialStatusBanner';
import { LoadingOverlay } from './LoadingOverlay';

export const Chat: React.FC<ChatProps> = ({
  messages,
  onSendMessage,
  onRetry,
  isLoading,
  error,
  isTyping,
  isCollapsed,
  onToggleCollapse,
  isStreaming,
  isAuthenticating,
  suggestions = [],
  progressData,
  trialState = 'active',
  isTrial = false,
  trialDaysRemaining,
  currentOrganizationId,
}) => {
  const AnimatePresenceFixedType = AnimatePresence as any; // This fixes an annoying typescript error
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSidePanel, setIsSidePanel] = useState(false);
  const [isChatMode, setIsChatMode] = useState(true);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  // Pick 3 random questions once when component mounts
  const defaultSuggestedQuestions = useMemo(() => {
    return DEFAULT_SUGGESTED_QUESTIONS.sort(() => Math.random() - 0.5).slice(0, 3);
  }, []);
  const [activeSentenceIndex, setActiveSentenceIndex] = useState(0);

  useEffect(() => {
    if (!progressData?.waitingSentences?.length) {
      setActiveSentenceIndex(0);
      return;
    }

    // Cycle through waiting_sentences every 4s
    const interval = setInterval(() => {
      setActiveSentenceIndex(prev =>
        (prev + 1) % progressData.waitingSentences!.length
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [progressData?.waitingSentences]);

  const currentSentence = progressData?.waitingSentences
    ? progressData.waitingSentences[activeSentenceIndex]
    : undefined;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia('(max-width: 600px)').matches);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (suggestion?: string) => {
    if (isStreaming) {
      return;
    }

    // Check input length
    if (input.length > 1000) {
      alert('Message is too long, please shorten it, the limit is 1000 characters');
      return;
    }
    
    if (suggestion) {
      setInput('');
      await onSendMessage(suggestion);
    } else if (input.trim()) {
      try {
        setInput('');
        await onSendMessage(input.trim());
      } catch (error) {
        console.error('Failed to send message:', error);
      }
    }
  };

  const handleViewMode = (mode: 'chat' | 'sidepanel' | 'fullscreen') => {
    if (mode === 'chat') {
      // When dock mode is deactivated
      window.dispatchEvent(new CustomEvent('appmanager-widget-dock-mode', { 
        detail: { docked: false } 
      }));
      setIsSidePanel(false);
      setIsFullscreen(false);
      setIsChatMode(true);
    } else if (mode === 'sidepanel') {
      // Send a message to the parent to tell it to adjust the dimesions to fit the widget
      window.dispatchEvent(new CustomEvent('appmanager-widget-dock-mode', { 
        detail: { docked: true } 
      }));
      setIsSidePanel(true);
      setIsFullscreen(false);
      setIsChatMode(false);
    } else {
      // When dock mode is deactivated
      window.dispatchEvent(new CustomEvent('appmanager-widget-dock-mode', { 
        detail: { docked: false } 
      }));
      setIsFullscreen(true);
      setIsChatMode(false);
      setIsSidePanel(false);
    }
  };

  const handleCollapse = () => {
    // When dock mode is deactivated
    window.dispatchEvent(new CustomEvent('appmanager-widget-dock-mode', { 
      detail: { docked: false } 
    }));
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onToggleCollapse?.();
    }, 300);
  };

  if (isCollapsed) {
    return (
      <FloatingButton
        initial={{ scale: 0.3, opacity: 0 }}
        animate={{ 
          scale: 1, 
          opacity: 1,
          rotate: [0, 20, -20, 10, -10, 0],
        }}
        exit={{ scale: 0.3, opacity: 0 }}
        transition={{ 
          duration: 0.3, 
          ease: [0.4, 0, 0.2, 1],
          rotate: {
            duration: 0.5,
            delay: 0.3,
            ease: "easeInOut"
          }
        }}
        whileHover={{ 
          scale: 1.1,
          transition: { duration: 0.2 }
        }}
        whileTap={{ scale: 0.95 }}
      >
        <Tooltip title={messages.length > 1 ? t('activeConversation') : t('expandChat')}>
          <IconButton
            onClick={() => onToggleCollapse?.(true, isSidePanel ? 'sidepanel' : 'chat')}
            sx={{
              backgroundColor: (theme) => theme.customProps.primaryColor,
              color: 'white',
              width: '56px',
              height: '56px',
              '&:hover': {
                backgroundColor: (theme) => theme.customProps.primaryColor,
                opacity: 0.9,
              },
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              position: 'relative',
            }}
            aria-label={t('expandChat')}
          >
            <SmartToyOutlinedIcon sx={{ fontSize: '28px' }} />
            {messages.length > 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#4caf50',
                  border: '2px solid white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
                aria-label={t('activeConversation')}
              />
            )}
          </IconButton>
        </Tooltip>
      </FloatingButton>
    );
  }

  // Check if trial status is still loading
  const isTrialLoading = trialState === 'loading' || isLoading || isAuthenticating;

  // Check if user needs trial overlay (doesn't have access)
  // Note: 'eligible' state should not show overlay as auto-activation is in progress
  const showTrialOverlay = trialState === 'expired' || trialState === 'not_eligible';

  // Check if trial banner should be visible
  const isTrialBannerVisible = isTrial && trialDaysRemaining !== null && trialDaysRemaining !== undefined && !showTrialOverlay;

  return (
    <ChatContainer
      elevation={3}
      role="region"
      aria-label={t('chatInterface')}
      isFullscreen={isFullscreen}
      isSidePanel={isSidePanel}
      className={isClosing ? 'closing' : ''}
    >
      <ChatHeader
        isFullscreen={isFullscreen}
        isSidePanel={isSidePanel}
        setIsFullscreen={setIsFullscreen}
        setSidePanel={setIsSidePanel}
        onToggleCollapse={handleCollapse}
        onViewModeChange={handleViewMode}
        isMobile={isMobile}
        messages={messages}
      />

      {/* Trial Status Banner - shown when user has active trial */}
      {isTrialBannerVisible && (
        <TrialStatusBanner 
          daysRemaining={trialDaysRemaining!}
          organizationId={currentOrganizationId}
          isFullscreen={isFullscreen}
        />
      )}

      {/* Loading Overlay - shown while checking trial status */}
      {isTrialLoading && <LoadingOverlay />}

      {/* Trial Overlay - shown when user doesn't have access */}
      {showTrialOverlay && (
        <TrialOverlay
          trialState={trialState}
          onCollapse={handleCollapse}
          organizationId={currentOrganizationId}
        />
      )}

      <MessageList role="log">
        <AnimatePresenceFixedType>
          {messages.map((message, index) => (
            <ChatMessage
              key={message.id}
              message={message}
              isFirstMessage={index === 0}
              isFullscreen={isFullscreen}
              isSidePanel={isSidePanel}
              isChatMode={isChatMode}
              onRetry={onRetry}
              hasError={!!error && index === messages.length - 1}
              progressData={progressData}
              currentSentence={currentSentence}
              question={message.sender === 'ai' && messages.length > 1? messages[index - 1]?.content : undefined}
              isTrial={isTrial}
              organizationId={currentOrganizationId}
              isTrialBannerVisible={isTrialBannerVisible}
              trialDaysRemaining={trialDaysRemaining ?? undefined}
            />
          ))}
        </AnimatePresenceFixedType>
        <div ref={messagesEndRef} />
      </MessageList>

      {!showTrialOverlay && (messages.length === 1 || messages[messages.length - 1]?.sender === 'ai') && (
        <Suggestions
          suggestions={messages.length === 1 ? defaultSuggestedQuestions : suggestions}
          onSuggestionClick={handleSend}
          disabled={isLoading || isStreaming}
          onSuggestionEdit={(suggestion) => setInput(suggestion)}
          isFullscreen={isFullscreen}
        />
      )}

      {error && !showTrialOverlay && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Typography color="error" px={2}>
            {error}
          </Typography>
        </motion.div>
      )}

      <InputContainer>
          <TextField
            fullWidth
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={isStreaming ? t('waitingForResponse') : t('typeMessage')}
            disabled={isLoading || isAuthenticating || isStreaming}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !isStreaming) {
                e.preventDefault();
                handleSend();
              }
            }}
            multiline
            maxRows={4}
          aria-label={t('messageInput')}
          />
        <Tooltip 
          title={t('sendMessage')}
          PopperProps={{
            disablePortal: false,
            sx: { zIndex: 99999999 }
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
              onClick={()=>handleSend()}
              disabled={isLoading || !input.trim() || isStreaming || isAuthenticating}
            color="primary"
              aria-label={t('sendMessage')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '40px',
                width: '40px',
                minWidth: '40px',
                borderRadius: '50%',
                '&:hover': {
                  borderRadius: '50%'
                }
              }}
          >
            {isLoading ? <CircularProgress size={24} /> : <Send />}
          </IconButton>
          </span>
        </Tooltip>
      </InputContainer>
    </ChatContainer>
  );
}; 